import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from 'components/ui/button';
import { ScanLinkChannel } from 'services/face-scan/type';

import ContinueScan from 'assets/images/face-scan/continue-scan.png';
import TwoScanning from 'assets/images/two-scanning.png';

export enum SelectedMode {
  QR = 'qr',
  WHATSAPP = ScanLinkChannel.WHATSAPP,
  EMAIL = ScanLinkChannel.EMAIL,
  APP = ScanLinkChannel.NOTIFICATION,
}

interface StartScanProps {
  onGenerate: () => Promise<string | undefined>;
  onWebSelect: () => void;
  setIsAppScan?: (isAppScan: boolean) => void;
  isGeneratingQR: boolean;
  isLoading: boolean;
}

export default function StartScan({
  onGenerate,
  isGeneratingQR,
  onWebSelect,
  isLoading,
  setIsAppScan,
}: StartScanProps) {
  const { t } = useTranslation();

  const onAppScan = async () => {
    const url = await onGenerate();
    if (!url) {
      toast.error(t('failed_to_open_app_msg'));
      return;
    }

    window.location.href = url;

    setTimeout(() => {
      window.open(url, '_blank');
    }, 2000);
    setIsAppScan?.(true);
  };

  return (
    <div className='space-y-12 min-h-[calc(100vh-7rem)] grid place-content-center max-w-5xl mx-auto py-8'>
      <div className='flex-col items-center justify-center hidden px-5 space-y-4 leading-tight text-center lg:flex'>
        <p>🌟 {t('get_most_accureate_result')}</p>
      </div>

      <div className='flex flex-col items-center justify-center gap-6 lg:gap-12 place-items-center lg:place-items-stretch lg:flex'>
        <div className='flex-col items-center justify-center hidden gap-6 lg:flex'>
          <div className='flex flex-col justify-center space-y-8 w-fit'>
            <div className='w-full h-28 lg:h-40'>
              <img
                src={TwoScanning}
                alt='scanning-desktop'
                className='object-contain w-full h-full'
              />
            </div>

            <div className='max-w-lg'>
              <p className='font-semibold leading-none text-center text-primary'>
                {t('mobile_scan_pro_msg')}
              </p>
            </div>

            <div className='flex justify-center mt-4'>
              <Button
                onClick={onGenerate}
                disabled={isGeneratingQR || isLoading}
              >
                {isGeneratingQR && (
                  <Loader2
                    role='progressbar'
                    className='mr-2 animate-spin size-4'
                  />
                )}
                {t('scan_recommended_device_msg')}
              </Button>
            </div>
          </div>
        </div>

        <div className='flex flex-col justify-center space-y-8 w-fit lg:hidden'>
          <div className='w-full h-28'>
            <img
              src={TwoScanning}
              alt='scanning-mobile'
              className='object-contain w-full h-full'
            />
          </div>

          <div className='flex justify-center'>
            <Button onClick={onAppScan} disabled={isLoading}>
              {isLoading && (
                <Loader2
                  role='progressbar'
                  className='mr-2 size-4 animate-spin'
                />
              )}
              {t('go_to_the_app')}
            </Button>
          </div>
        </div>

        <div className='flex items-center justify-center h-full lg:hidden '>
          <p className='text-lg font-semibold'>{t('or')}</p>
        </div>

        <div className='flex flex-col justify-center space-y-8 lg:hidden w-fit'>
          <div className='w-full h-28 lg:h-40'>
            <img
              src={ContinueScan}
              alt='continue scan'
              className='object-contain w-full h-full'
            />
          </div>
          <div className='flex justify-center'>
            <Button
              onClick={onWebSelect}
              disabled={isLoading || isGeneratingQR}
            >
              {t('continue_with_webcam')}
            </Button>
          </div>
        </div>
      </div>

      <p className='hidden font-medium text-center lg:block'>
        {t('scan_privacy_info')}
      </p>
    </div>
  );
}
